import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Phone as PhoneIcon,
    MessageSquare as MessageSquareIcon,
    MessageCircleMore as MessageCircleMoreIcon,
    Save as SaveIcon,
    X as XIcon,
    SquarePen as SquarePenIcon,
    Ban as BanIcon,
    CircleSlash as CircleSlashIcon,
    User as UserIcon,
    Info as InfoIcon,
} from 'lucide-react';

import {
    setSmsNumbersData,
    setWebcallNumbersData,
} from '@/store/numberManagementSlice';

import {
    getEncryptedSSOData,
    fetchSmsNumbers,
    fetchWebcallNumbers,
    setSmsDefault,
    setFallbackBnum,
    setWebcallDefault,
} from '@/services/ghlService';

import Card from '@/components/Card/Card';
import Button from '@/components/Button/Button';
import Select from '@/components/Select/Select';
import Loading from '@/components/Loading/Loading';
import SmartTooltip from '@/components/Tooltip/SmartTooltip';
import ErrorMessage from '@/components/ErrorMessage/ErrorMessage';
import Modal from '@/components/Modal/Modal';

const NumberManagement = () => {
    // --- State: Loading ---
    const [loading, setLoading] = useState(false);

    // --- State: Redux ---
    const dispatch = useDispatch();
    const numberManagementState = useSelector(
        (state) => state.numberManagement
    );
    const { sms: smsStore, webcall: webcallStore } = numberManagementState;
    const userState = useSelector((state) => state.user);
    const { userData } = userState;

    // --- State: Forms & Modals ---
    const [showEditForm, setShowEditForm] = useState(false);
    const [showFallbackEditForm, setShowFallbackEditForm] = useState(false);
    const [showOverrideConfirm, setShowOverrideConfirm] = useState(false);

    // --- State: Errors ---
    const [smsError, setSmsError] = useState(null);
    const [webcallError, setWebcallError] = useState(null);

    // --- Utility Functions ---
    function isAgencyOrAccountAdmin() {
        return (
            userData?.role === 'admin' &&
            (userData?.type === 'agency' || userData?.type === 'account')
        );
    }

    // --- Local Error Handling ---
    function handleErrors(res, type) {
        if (type === 'sms') {
            setSmsError(res);
        } else if (type === 'webcall') {
            setWebcallError(res);
        }
    }

    // --- Load Data ---
    async function loadSmsNumbers(forceFetch) {
        try {
            setLoading(true);

            const encCode = await getEncryptedSSOData();

            const encCodeURI = encodeURIComponent(encCode);

            const smsResponse = await fetchSmsNumbers(
                numberManagementState,
                encCodeURI,
                forceFetch
            );

            if (smsResponse.status === 200) {
                dispatch(setSmsNumbersData(smsResponse.data));
            } else {
                dispatch(setSmsNumbersData('Not Available'));
                handleErrors(smsResponse, 'sms'); // when response code: 204
            }
        } catch (error) {
            handleErrors(error, 'sms'); // when response code: 404
        } finally {
            setLoading(false);
        }
    }

    async function loadWebcallNumbers(forceFetch) {
        try {
            setLoading(true);

            const encCode = await getEncryptedSSOData();

            const encCodeURI = encodeURIComponent(encCode);

            const webcallResponse = await fetchWebcallNumbers(
                numberManagementState,
                encCodeURI,
                forceFetch
            );

            if (webcallResponse.status === 200) {
                dispatch(setWebcallNumbersData(webcallResponse.data));
            } else {
                dispatch(setWebcallNumbersData('Not Available'));
                handleErrors(webcallResponse, 'webcall'); // when response code: 204
            }
        } catch (error) {
            handleErrors(error, 'webcall'); // when response code: 404
        } finally {
            setLoading(false);
        }
    }

    async function loadAll() {
        setLoading(true);

        try {
            await Promise.all([
                loadSmsNumbers(false),
                loadWebcallNumbers(false),
            ]);
        } finally {
            setLoading(false);
            setShowEditForm(false);
        }
    }

    // --- useEffects ---
    useEffect(() => {
        if (!userData) return;

        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData]);

    // --- Action Handlers ---
    async function handleSaveConfiguration(config) {
        setShowEditForm(false);
        setLoading(true);
        const encCode = await getEncryptedSSOData();

        try {
            const promises = [];

            if (
                Boolean(config.selectedSmsDefault) &&
                smsStore.selected_bnum !== config.selectedSmsDefault
            ) {
                promises.push(() =>
                    setSmsDefault({
                        encryptedSSOData: encCode,
                        bnum: config.selectedSmsDefault,
                    }).then((res) => {
                        if (res.status === 200) {
                            return loadSmsNumbers(true);
                        }
                    })
                );
            }

            if (
                Boolean(config.selectedWebcallDefault) &&
                webcallStore.selected_bnum !== config.selectedWebcallDefault
            ) {
                promises.push(() =>
                    setWebcallDefault({
                        encryptedSSOData: encCode,
                        bnum: config.selectedWebcallDefault,
                    }).then((res) => {
                        if (res.status === 200) {
                            return loadWebcallNumbers(true);
                        }
                    })
                );
            }

            await Promise.all(promises.map((fn) => fn()));
        } finally {
            setLoading(false);
        }
    }

    async function handleSaveFallbackConfiguration(config) {
        setShowFallbackEditForm(false);
        setLoading(true);
        const encCode = await getEncryptedSSOData();

        try {
            if (
                Boolean(config.selectedFallback) &&
                (smsStore.fallback_number_details.fallback_bnum !==
                    config.selectedFallback ||
                    smsStore.fallback_number_details?.user_id !==
                        userData.userId)
            ) {
                const res = await setFallbackBnum({
                    encryptedSSOData: encCode,
                    bnumFallback: config.selectedFallback,
                });
                if (res.status === 200) {
                    await loadSmsNumbers(true);
                }
            }
        } finally {
            setLoading(false);
        }
    }

    function handleOverrideConfirmation() {
        setShowOverrideConfirm(false);
        setShowFallbackEditForm(true);
    }

    // --- Components: Default Numbers ---
    function SectionHeader({ type, label, Icon }) {
        return (
            <div className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                <Icon
                    className={`h-4 w-4 mr-2 ${type === 'sms' ? 'text-green-600' : 'text-blue-600'}`}
                />
                {label}
            </div>
        );
    }

    function RenderDefaultSection({
        type,
        store,
        error,
        label,
        loading,
        Icon,
    }) {
        if (loading) {
            return (
                <div>
                    <SectionHeader label={label} Icon={Icon} type={type} />
                    <div className="ms-4">
                        <Loading />
                    </div>
                </div>
            );
        }

        if (error || store === 'Not Available') {
            return (
                <div>
                    <SectionHeader label={label} Icon={Icon} type={type} />
                    {error?.status === 204 || store === 'Not Available' ? (
                        <SmartTooltip
                            content={`No numbers allocated for ${type === 'sms' ? 'SMS' : 'Voice'}`}
                        >
                            <div className="w-full flex items-center gap-3 bg-secondary-300 text-secondary-700 rounded-xl p-2 shadow-sm">
                                <BanIcon className="h-4 w-4 text-secondary-500 mt-0.5" />
                                <div>
                                    <p className="text-xs">Not Available</p>
                                </div>
                            </div>
                        </SmartTooltip>
                    ) : (
                        <ErrorMessage />
                    )}
                </div>
            );
        }

        if (!store.has_selected_bnum) {
            return (
                <div>
                    <SectionHeader label={label} Icon={Icon} type={type} />
                    <SmartTooltip
                        content={`No number has been set as the default ${type === 'sms' ? 'SMS' : 'Voice'} number.`}
                    >
                        <div className="w-full flex items-center gap-3 bg-warning-100 text-warning-700 rounded-xl p-2 shadow-sm">
                            <CircleSlashIcon className="h-4 w-4 text-warning-400 mt-0.5" />
                            <div>
                                <p className="text-xs">Not Set</p>
                            </div>
                        </div>
                    </SmartTooltip>
                </div>
            );
        }
        return (
            <div>
                <SectionHeader label={label} Icon={Icon} type={type} />
                <p className="text-sm text-gray-600">
                    {store?.selected_bnum || 'Something Went Wrong'}
                </p>
            </div>
        );
    }

    function CurrentUserDefaultNumbers() {
        return (
            <Card>
                <Card.Header divider>
                    <div className="text-md font-medium">My Phone Numbers</div>
                </Card.Header>

                <div className="grid md:grid-cols-3 gap-4 p-4">
                    {/* SMS Section */}
                    <RenderDefaultSection
                        type="sms"
                        error={smsError}
                        store={smsStore}
                        loading={loading}
                        label="SMS Number"
                        Icon={MessageSquareIcon}
                    />

                    {/* Webcall Section */}
                    <RenderDefaultSection
                        type="webcall"
                        error={webcallError}
                        store={webcallStore}
                        loading={loading}
                        label="Voice Number"
                        Icon={PhoneIcon}
                    />

                    {/* Button Section */}
                    <div className="flex items-center justify-end">
                        <Button
                            variant={showEditForm ? 'secondary' : 'primary'}
                            icon={showEditForm ? XIcon : SquarePenIcon}
                            onClick={() => setShowEditForm(!showEditForm)}
                            disabled={loading || (webcallError && smsError)}
                        >
                            Edit
                        </Button>
                    </div>
                </div>
            </Card>
        );
    }

    function EditForm({ onSave }) {
        const [selectedWebcallDefault, setSelectedWebcallDefault] = useState(
            webcallStore.selected_bnum || null
        );

        const [selectedSmsDefault, setSelectedSmsDefault] = useState(
            smsStore.selected_bnum || null
        );

        function handleSaveProcess() {
            onSave({
                selectedSmsDefault,
                selectedWebcallDefault,
            });
        }

        return (
            <>
                <Card className="mt-2">
                    <div className="flex flex-col p-4">
                        <div className="grid md:grid-cols-2 gap-4">
                            {smsError || smsStore === 'Not Available' ? (
                                smsError?.status === 204 ||
                                smsStore === 'Not Available' ? (
                                    <SmartTooltip
                                        content="No numbers allocated for SMS"
                                        containerClassName="w-full"
                                    >
                                        <Select
                                            label="SMS"
                                            inputValue={[]}
                                            options={[]}
                                            loading={loading}
                                            disabled
                                        />
                                    </SmartTooltip>
                                ) : (
                                    <ErrorMessage />
                                )
                            ) : (
                                <Select
                                    label="SMS"
                                    options={smsStore.bnum_list}
                                    labelKey="bnum"
                                    valueKey="bnum"
                                    searchProps={{
                                        enabled: true,
                                        keyArray: ['bnum', 'adName'],
                                    }}
                                    inputValue={selectedSmsDefault}
                                    getLabel={(item) => {
                                        if (!item) return null;
                                        return (
                                            <div className="flex flex-wrap items-center">
                                                <span className="me-2 text-sm">
                                                    {item.bnum}
                                                </span>
                                                {item.adName ? (
                                                    <span className="font-light text-xs text-secondary-500">
                                                        ({item.adName})
                                                    </span>
                                                ) : null}
                                            </div>
                                        );
                                    }}
                                    onChange={(val) =>
                                        setSelectedSmsDefault(val)
                                    }
                                />
                            )}
                            {webcallError ||
                            webcallStore === 'Not Available' ? (
                                webcallError?.status === 204 ||
                                webcallStore === 'Not Available' ? (
                                    <SmartTooltip
                                        content="No numbers allocated for Voice"
                                        containerClassName="w-full"
                                    >
                                        <Select
                                            label="Voice"
                                            inputValue={[]}
                                            options={[]}
                                            loading={loading}
                                            disabled
                                        />
                                    </SmartTooltip>
                                ) : (
                                    <ErrorMessage />
                                )
                            ) : (
                                <Select
                                    label="Voice"
                                    options={webcallStore.bnum_list}
                                    labelKey="bnum"
                                    valueKey="bnum"
                                    searchProps={{
                                        enabled: true,
                                        keyArray: ['bnum', 'adName'],
                                    }}
                                    inputValue={selectedWebcallDefault}
                                    getLabel={(item) => {
                                        if (!item) return null;

                                        return (
                                            <div className="flex flex-wrap items-center">
                                                <span className="me-2 text-sm">
                                                    {item.bnum}
                                                </span>
                                                {item.adName ? (
                                                    <span className="font-light text-xs text-secondary-500">
                                                        ({item.adName})
                                                    </span>
                                                ) : null}
                                            </div>
                                        );
                                    }}
                                    onChange={(val) =>
                                        setSelectedWebcallDefault(val)
                                    }
                                />
                            )}
                        </div>
                    </div>

                    <Card.Footer divider>
                        <div className="flex items-center justify-end gap-4">
                            <Button
                                variant="secondary"
                                icon={XIcon}
                                onClick={() => setShowEditForm(false)}
                            >
                                Cancel
                            </Button>

                            <Button
                                variant="primary"
                                icon={SaveIcon}
                                onClick={handleSaveProcess}
                                disabled={
                                    !selectedSmsDefault &&
                                    !selectedWebcallDefault
                                }
                            >
                                Save
                            </Button>
                        </div>
                    </Card.Footer>
                </Card>
            </>
        );
    }

    // --- Components: Fallback Numbers ---
    function RenderFallbackSection({ store, error, label, loading, Icon }) {
        if (loading) {
            return (
                <div>
                    <SectionHeader label={label} Icon={Icon} />
                    <div className="ms-4">
                        <Loading />
                    </div>
                </div>
            );
        }

        if (error || store === 'Not Available') {
            return (
                <div>
                    <SectionHeader label={label} Icon={Icon} />
                    {error?.status === 204 || store === 'Not Available' ? (
                        <SmartTooltip content="No numbers allocated for fallback SMS">
                            <div className="w-full flex items-center gap-3 bg-secondary-300 text-secondary-700 rounded-xl p-2 shadow-sm">
                                <BanIcon className="h-4 w-4 text-secondary-500 mt-0.5" />
                                <div>
                                    <p className="text-xs">Not Available</p>
                                </div>
                            </div>
                        </SmartTooltip>
                    ) : (
                        <ErrorMessage />
                    )}
                </div>
            );
        }

        if (store?.fallback_number_details?.fallback_bnum === '') {
            return (
                <div>
                    <SectionHeader label={label} Icon={Icon} />
                    <SmartTooltip content="No number has been set as the fallback number.">
                        <div className="w-full flex items-center gap-3 bg-warning-100 text-warning-700 rounded-xl p-2 shadow-sm">
                            <CircleSlashIcon className="h-4 w-4 text-warning-400 mt-0.5" />
                            <div>
                                <p className="text-xs">Not Set</p>
                            </div>
                        </div>
                    </SmartTooltip>
                </div>
            );
        }

        return (
            <div>
                <SectionHeader label={label} Icon={Icon} />
                <p className="text-sm text-gray-600">
                    {store?.fallback_number_details?.fallback_bnum ||
                        'Something Went Wrong'}
                </p>
            </div>
        );
    }

    function FallbackRender() {
        if (!isAgencyOrAccountAdmin()) return null;

        if (smsStore.fallback_number_details?.fallback_bnum || loading) {
            return (
                <Card className="mt-4">
                    <Card.Header divider>
                        <div className="text-md font-medium flex items-center gap-2">
                            Account Phone Number
                            <SmartTooltip content="Assign a phone number for Bulk SMS & Automations">
                                <InfoIcon className="h-4 w-4 text-gray-500" />
                            </SmartTooltip>
                        </div>
                    </Card.Header>

                    <div className="grid md:grid-cols-2 gap-4 p-4">
                        <RenderFallbackSection
                            error={smsError}
                            store={smsStore}
                            loading={loading}
                            label="SMS Number"
                            Icon={MessageCircleMoreIcon}
                        />

                        <div className="flex items-center justify-end">
                            <Button
                                variant={
                                    showFallbackEditForm
                                        ? 'secondary'
                                        : smsStore.fallback_number_details
                                                ?.user_id &&
                                            smsStore.fallback_number_details
                                                ?.user_id !== userData.userId
                                          ? 'warning'
                                          : 'primary'
                                }
                                icon={
                                    showFallbackEditForm ? XIcon : SquarePenIcon
                                }
                                onClick={() => {
                                    if (showFallbackEditForm) {
                                        setShowFallbackEditForm(false);
                                    } else if (
                                        smsStore.fallback_number_details
                                            ?.user_id !== userData.userId
                                    ) {
                                        setShowOverrideConfirm(true);
                                    } else {
                                        setShowFallbackEditForm(true);
                                    }
                                }}
                                disabled={loading || smsError}
                            >
                                {smsStore.fallback_number_details?.user_id &&
                                smsStore.fallback_number_details?.user_id !==
                                    userData.userId
                                    ? 'Admin Override'
                                    : 'Edit'}
                            </Button>
                        </div>
                    </div>
                </Card>
            );
        }

        return (
            <div className="mt-4">
                <Button
                    variant={showFallbackEditForm ? 'secondary' : 'success'}
                    icon={showFallbackEditForm ? XIcon : SquarePenIcon}
                    onClick={() =>
                        setShowFallbackEditForm(!showFallbackEditForm)
                    }
                    disabled={loading || (webcallError && smsError)}
                >
                    Assign Fallback Number
                </Button>
            </div>
        );
    }

    function EditFallbackForm({ onSave }) {
        const [selectedFallback, setSelectedFallback] = useState(
            smsStore.fallback_number_details?.fallback_bnum || null
        );

        function handleSaveProcess() {
            onSave({ selectedFallback });
        }

        return (
            <Card className="mt-2">
                <div className="grid md:grid-cols-2 p-4">
                    {smsError || smsStore === 'Not Available' ? (
                        smsError?.status === 204 ||
                        smsStore === 'Not Available' ? (
                            <SmartTooltip
                                content="No numbers allocated for SMS"
                                containerClassName="w-full"
                            >
                                <Select
                                    label="SMS"
                                    inputValue={[]}
                                    options={[]}
                                    loading={loading}
                                    disabled
                                />
                            </SmartTooltip>
                        ) : (
                            <ErrorMessage />
                        )
                    ) : (
                        <Select
                            label="SMS"
                            options={smsStore.bnum_list}
                            labelKey="bnum"
                            valueKey="bnum"
                            searchProps={{
                                enabled: true,
                                keyArray: ['bnum', 'adName'],
                            }}
                            inputValue={selectedFallback}
                            getLabel={(item) => {
                                if (!item) return null;
                                return (
                                    <div className="flex flex-wrap items-center">
                                        <span className="me-2 text-sm">
                                            {item.bnum}
                                        </span>
                                        {item.adName ? (
                                            <span className="font-light text-xs text-secondary-500">
                                                ({item.adName})
                                            </span>
                                        ) : null}
                                    </div>
                                );
                            }}
                            onChange={(val) => setSelectedFallback(val)}
                        />
                    )}
                </div>

                <Card.Footer divider>
                    <div className="flex items-center justify-end gap-4">
                        <Button
                            variant="secondary"
                            icon={XIcon}
                            onClick={() => setShowFallbackEditForm(false)}
                        >
                            Cancel
                        </Button>

                        <Button
                            variant="primary"
                            icon={SaveIcon}
                            onClick={handleSaveProcess}
                            disabled={!selectedFallback}
                        >
                            Save
                        </Button>
                    </div>
                </Card.Footer>
            </Card>
        );
    }

    // --- Components: Modals ---
    function OverrideConfirmModal() {
        return (
            <Modal
                width="500px"
                isOpen={showOverrideConfirm}
                onClose={() => setShowOverrideConfirm(false)}
                title="Override Fallback Number"
            >
                <div className="max-h-[600px] flex flex-col gap-4">
                    <div className="flex flex-col gap-4 text-secondary">
                        <div>
                            The number has been configured by another user.
                        </div>
                        Do you want to override the configuration?
                    </div>

                    <div className="flex justify-end gap-2 pt-4">
                        <Button
                            appearance="outline"
                            variant="secondary"
                            size="md"
                            onClick={() => setShowOverrideConfirm(false)}
                        >
                            NO
                        </Button>

                        <Button
                            variant="danger"
                            size="md"
                            onClick={handleOverrideConfirmation}
                        >
                            YES, Override
                        </Button>
                    </div>
                </div>
            </Modal>
        );
    }

    // --- Render ---
    return (
        <>
            <CurrentUserDefaultNumbers />

            {showEditForm && <EditForm onSave={handleSaveConfiguration} />}

            <FallbackRender />

            {showFallbackEditForm && (
                <EditFallbackForm onSave={handleSaveFallbackConfiguration} />
            )}

            <OverrideConfirmModal />
        </>
    );
};

export default NumberManagement;
